import { useState } from 'react';
import { useI18n } from '../lib/i18n';
import { cn } from '../lib/cn';
import { useAccounts } from '../lib/accounts';
import Modal from '../components/ui/Modal';
import Toggle from '../components/ui/Toggle';
import { Skeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import {
  useProducts, saveProduct, deleteProduct, productMessage,
  connectCatalog, disconnectCatalog, syncAllCatalogProducts,
  type Product, type ProductInput,
} from '../lib/catalog';

const inputCls = 'w-full rounded-[9px] border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary dark:border-slate-700 dark:bg-slate-800 dark:text-white';
const empty: ProductInput = { name: '', description: '', price: 0, currency: 'OMR', imageUrl: '', sku: '', inStock: true };

export default function ECommerceCatalog() {
  const { t } = useI18n();
  const accounts = useAccounts();
  const activeIntegration = accounts.selected;
  const activeCatalogId = activeIntegration?.catalogId;

  const { products, loading } = useProducts();
  const [editing, setEditing] = useState<ProductInput | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [confirmDel, setConfirmDel] = useState<Product | null>(null);

  // Meta Catalog Sync state
  const [catalogInput, setCatalogInput] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [metaErr, setMetaErr] = useState<string | null>(null);
  const [syncResult, setSyncResult] = useState<{ total: number; synced: number; failed: number } | null>(null);

  function openNew() { setErr(null); setEditing({ ...empty }); }
  function openEdit(p: Product) {
    setErr(null);
    setEditing({ id: p.id, name: p.name, description: p.description ?? '', price: p.price, currency: p.currency, imageUrl: p.imageUrl ?? '', sku: p.sku ?? '', inStock: p.inStock });
  }

  async function save() {
    if (!editing || !editing.name.trim()) return;
    setBusy(true); setErr(null);
    try {
      await saveProduct({
        ...editing,
        description: editing.description?.trim() || undefined,
        imageUrl: editing.imageUrl?.trim() || undefined,
        sku: editing.sku?.trim() || undefined,
      });
      setEditing(null);
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }

  async function doDelete() {
    if (!confirmDel) return;
    setBusy(true);
    try { await deleteProduct(confirmDel.id); setConfirmDel(null); }
    catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }

  async function copyMsg(p: Product) {
    try { await navigator.clipboard.writeText(productMessage(p)); setCopied(p.id); window.setTimeout(() => setCopied(null), 1500); } catch { /* noop */ }
  }

  async function handleConnectCatalog(e: React.FormEvent) {
    e.preventDefault();
    if (!catalogInput.trim() || connecting) return;
    setConnecting(true);
    setMetaErr(null);
    try {
      await connectCatalog({
        integrationId: activeIntegration?.id,
        catalogId: catalogInput.trim(),
      });
      setCatalogInput('');
      await accounts.refresh();
    } catch (e: any) {
      setMetaErr(e?.message || 'Failed to connect Meta catalog');
    } finally {
      setConnecting(false);
    }
  }

  async function handleDisconnectCatalog() {
    if (disconnecting) return;
    setDisconnecting(true);
    setMetaErr(null);
    try {
      await disconnectCatalog({ integrationId: activeIntegration?.id });
      setSyncResult(null);
      await accounts.refresh();
    } catch (e: any) {
      setMetaErr(e?.message || 'Failed to disconnect Meta catalog');
    } finally {
      setDisconnecting(false);
    }
  }

  async function handleSyncAll() {
    if (syncing) return;
    setSyncing(true);
    setMetaErr(null);
    try {
      const res = await syncAllCatalogProducts({ integrationId: activeIntegration?.id });
      setSyncResult(res);
    } catch (e: any) {
      setMetaErr(e?.message || 'Failed to sync products with Meta catalog');
    } finally {
      setSyncing(false);
    }
  }

  return (
    <div>
      <div className="mb-1 flex flex-wrap items-center gap-3">
        <h1 className="text-[22px] font-extrabold text-ink dark:text-white">{t('cat.title')}</h1>
        <button type="button" onClick={openNew} className="rounded-[9px] bg-primary px-4 py-2 text-[13px] font-bold text-white sm:ms-auto hover:bg-primary-dark transition">+ {t('cat.add')}</button>
      </div>
      <p className="mb-5 text-ink-3 dark:text-slate-400">{t('cat.subtitle')}</p>

      {/* Meta Commerce Catalog Sync Card (WP-21b / WP-22) */}
      <div className="mb-6 rounded-card border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-850">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="space-y-1 max-w-xl">
            <div className="flex items-center gap-2">
              <span className="text-xl">🛍️</span>
              <h2 className="text-sm font-bold text-ink dark:text-white">
                {t('cat.meta.title') || 'Meta Commerce Catalog'}
              </h2>
              {activeCatalogId ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300">
                  ● {t('cat.meta.connected') || 'Connected'}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                  ○ {t('cat.meta.disconnected') || 'Not Connected'}
                </span>
              )}
            </div>
            <p className="text-xs text-ink-3 dark:text-slate-400">
              {t('cat.meta.desc') || 'Connect your Meta Commerce Catalog ID to enable interactive WhatsApp product cards, multi-product lists, and automated catalog syncing.'}
            </p>
          </div>

          {activeCatalogId ? (
            <div className="flex flex-wrap items-center gap-2.5">
              <div className="rounded-lg bg-slate-100 dark:bg-slate-800 px-3 py-1.5 text-xs text-ink-2 dark:text-slate-300">
                <span className="font-semibold text-ink-3 dark:text-slate-400 me-1.5">{t('cat.meta.catalogId') || 'ID'}:</span>
                <span className="font-mono font-bold text-ink dark:text-white">{activeCatalogId}</span>
              </div>
              <button
                type="button"
                onClick={handleSyncAll}
                disabled={syncing}
                className="flex items-center gap-1.5 rounded-[9px] bg-primary hover:bg-primary-dark px-3 py-1.5 text-xs font-bold text-white transition disabled:opacity-50"
              >
                {syncing ? '…' : '🔄'} {t('cat.meta.syncAll') || 'Sync All'}
              </button>
              <button
                type="button"
                onClick={handleDisconnectCatalog}
                disabled={disconnecting}
                className="rounded-[9px] border border-rose-200 px-3 py-1.5 text-xs font-bold text-rose-600 hover:bg-rose-50 dark:border-rose-900/60 dark:text-rose-400 dark:hover:bg-rose-950/30 transition disabled:opacity-50"
              >
                {disconnecting ? '…' : t('cat.meta.disconnect') || 'Disconnect'}
              </button>
            </div>
          ) : (
            <form onSubmit={handleConnectCatalog} className="flex items-center gap-2 w-full md:w-auto">
              <input
                type="text"
                value={catalogInput}
                onChange={(e) => setCatalogInput(e.target.value)}
                placeholder={t('cat.meta.placeholder') || 'Enter Meta Catalog ID (e.g. 1234567890)'}
                className="w-full md:w-64 rounded-[9px] border border-slate-200 bg-surface px-3 py-1.5 text-xs text-ink outline-none focus:border-primary dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                disabled={connecting}
              />
              <button
                type="submit"
                disabled={connecting || !catalogInput.trim()}
                className="rounded-[9px] bg-primary hover:bg-primary-dark px-3.5 py-1.5 text-xs font-bold text-white transition disabled:opacity-50 whitespace-nowrap shrink-0"
              >
                {connecting ? '…' : t('cat.meta.connect') || 'Connect'}
              </button>
            </form>
          )}
        </div>

        {metaErr && (
          <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 p-2.5 text-xs font-semibold text-rose-700 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-300">
            {metaErr}
          </div>
        )}

        {syncResult && (
          <div className="mt-3 flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-300">
            <span className="font-bold">✓ {t('cat.meta.syncSuccess') || 'Sync Complete'}:</span>
            <span>{t('cat.meta.syncResult', { total: syncResult.total, synced: syncResult.synced, failed: syncResult.failed }) || `Total: ${syncResult.total} · Synced: ${syncResult.synced} · Failed: ${syncResult.failed}`}</span>
          </div>
        )}
      </div>

      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="overflow-hidden rounded-card border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-850">
              <Skeleton className="aspect-[4/3] w-full rounded-lg" />
              <div className="mt-3 space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
                <Skeleton className="h-4 w-1/3" />
              </div>
            </div>
          ))}
        </div>
      ) : products.length === 0 ? (
        <div className="rounded-card border border-slate-200 bg-white p-8 shadow-sm dark:border-slate-800 dark:bg-slate-850">
          <EmptyState
            title={t('cat.empty')}
            description={t('cat.emptyDesc') || 'Add products to your catalog to share them directly in WhatsApp chats and automate sales.'}
            action={
              <button
                type="button"
                onClick={openNew}
                className="rounded-[9px] bg-primary px-4 py-2 text-[13px] font-bold text-white hover:bg-primary-dark transition"
              >
                + {t('cat.add')}
              </button>
            }
          />
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {products.map((p) => {
            const syncStatus = p.metaSync?.status;
            return (
              <div key={p.id} className="flex flex-col overflow-hidden rounded-card border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-850">
                <div className="aspect-[4/3] w-full bg-surface dark:bg-slate-800">
                  {p.imageUrl
                    ? <img src={p.imageUrl} alt={p.name} className="h-full w-full object-cover" />
                    : <div className="grid h-full place-items-center text-3xl text-ink-3">🛍️</div>}
                </div>
                <div className="flex flex-1 flex-col p-3">
                  <div className="flex items-start justify-between gap-2">
                    <b className="text-sm text-ink dark:text-white">{p.name}</b>
                    <span className={cn('whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold', p.inStock ? 'bg-primary-50 text-primary dark:bg-emerald-950/50 dark:text-emerald-300' : 'bg-slate-100 text-ink-3 dark:bg-slate-800 dark:text-slate-400')}>
                      {p.inStock ? t('cat.inStock') : t('cat.outStock')}
                    </span>
                  </div>

                  {/* Meta Sync Status Badge */}
                  <div className="mt-1 flex items-center gap-1.5">
                    {syncStatus === 'synced' ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
                        ✓ {t('cat.syncBadge.synced') || 'Synced'}
                      </span>
                    ) : syncStatus === 'failed' ? (
                      <div className="relative group inline-block">
                        <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-bold text-rose-700 dark:bg-rose-950/40 dark:text-rose-300 cursor-help">
                          ⚠️ {t('cat.syncBadge.failed') || 'Sync failed'}
                        </span>
                        <div className="pointer-events-none absolute bottom-full mb-1 start-0 z-20 hidden max-w-xs rounded bg-slate-900 px-2.5 py-1 text-[10px] text-white shadow-lg group-hover:block dark:bg-slate-800">
                          {p.metaSync?.error || t('cat.syncBadge.failed')}
                        </div>
                      </div>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                        {t('cat.syncBadge.notSynced') || 'Not synced'}
                      </span>
                    )}
                  </div>

                  <span className="mt-1 text-sm font-extrabold text-ink dark:text-white">{p.price} {p.currency}</span>
                  {p.description && <p className="mt-1 line-clamp-2 flex-1 text-xs text-ink-3 dark:text-slate-400">{p.description}</p>}
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button type="button" onClick={() => openEdit(p)} className="rounded-[8px] border border-slate-200 px-3 py-1.5 text-xs font-bold text-ink hover:border-primary dark:border-slate-700 dark:text-slate-200">{t('cat.edit')}</button>
                    <button type="button" onClick={() => void copyMsg(p)} className="rounded-[8px] border border-slate-200 px-3 py-1.5 text-xs font-bold text-primary hover:bg-primary-50 dark:border-slate-700 dark:hover:bg-slate-800">{copied === p.id ? t('set.api.copied') : t('cat.copyMsg')}</button>
                    <button type="button" onClick={() => setConfirmDel(p)} className="rounded-[8px] border border-red-500 px-3 py-1.5 text-xs font-bold text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30">{t('cat.delete')}</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/edit modal */}
      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing?.id ? t('cat.editTitle') : t('cat.add')}
        footer={
          <>
            <button type="button" onClick={() => setEditing(null)} className="rounded-[9px] border border-slate-200 bg-white px-4 py-2 text-[13px] font-bold text-ink">{t('common.cancel')}</button>
            <button type="button" onClick={save} disabled={busy || !editing?.name.trim()} className="rounded-[9px] bg-primary px-4 py-2 text-[13px] font-bold text-white disabled:opacity-40">{busy ? '…' : t('common.apply')}</button>
          </>
        }
      >
        {editing && (
          <div className="space-y-3">
            <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder={t('cat.name')} aria-label={t('cat.name')} className={inputCls} autoFocus />
            <textarea rows={2} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} placeholder={t('cat.description')} aria-label={t('cat.description')} className={inputCls} />
            <div className="grid grid-cols-2 gap-3">
              <input type="number" min={0} step="0.01" value={editing.price} onChange={(e) => setEditing({ ...editing, price: Number(e.target.value) })} placeholder={t('cat.price')} aria-label={t('cat.price')} className={inputCls} />
              <input value={editing.currency} onChange={(e) => setEditing({ ...editing, currency: e.target.value.toUpperCase() })} placeholder="OMR" aria-label="OMR" className={inputCls} />
            </div>
            <input value={editing.imageUrl} onChange={(e) => setEditing({ ...editing, imageUrl: e.target.value })} placeholder={t('cat.imageUrl')} aria-label={t('cat.imageUrl')} className={inputCls} />
            <input value={editing.sku} onChange={(e) => setEditing({ ...editing, sku: e.target.value })} placeholder={t('cat.sku')} aria-label={t('cat.sku')} className={inputCls} />
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-ink">{t('cat.inStock')}</span>
              <Toggle on={editing.inStock} onChange={(v) => setEditing({ ...editing, inStock: v })} label={t('cat.inStock')} />
            </div>
            {err && <div className="rounded-[9px] bg-red-50 px-3 py-2 text-xs font-semibold text-red-600">{err}</div>}
          </div>
        )}
      </Modal>

      {/* Delete confirm */}
      <Modal
        open={confirmDel !== null}
        onClose={() => setConfirmDel(null)}
        title={t('cat.delete')}
        footer={
          <>
            <button type="button" onClick={() => setConfirmDel(null)} className="rounded-[9px] border border-slate-200 bg-white px-4 py-2 text-[13px] font-bold text-ink">{t('common.cancel')}</button>
            <button type="button" onClick={doDelete} disabled={busy} className="rounded-[9px] bg-red-500 px-4 py-2 text-[13px] font-bold text-white disabled:opacity-40">{t('cat.delete')}</button>
          </>
        }
      >
        <p className="text-sm text-ink-2">{confirmDel?.name}</p>
      </Modal>
    </div>
  );
}
